// One release snapshot and one selection function serve static and React pages.
const CONDITIONER = {
  path: '/country/canada/bath-and-body/conditioner',
  category: 'Bath & Body', subcategory: 'Conditioner', pageSize: 20,
  title: 'Shop Conditioner Available in Canada | OriginSelect',
  description: 'Browse conditioner available to Canadian shoppers, including moisturizing, leave-in and colour-care formulas. Explore products and brand details on OriginSelect.',
  catalogPath: '/static-categories/canada/conditioner-catalog.json'
};
const ORIGIN = 'https://www.originselect.com';
const filterKeys = ['values', 'provinces', 'q', 'sort', 'minPrice', 'maxPrice'];
const slug = value => String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const escapeHtml = value => String(value == null ? '' : value).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
const productId = p => String(p.productId || p.productID || p._id || '');
function isConditionerLocation(path, params) {
  if (path.replace(/\/$/, '') === CONDITIONER.path) return true;
  if (!['/country/canada/bath-and-body','/country/canada/products'].includes(path)) return false;
  const exact = (key, value) => !params.has(key) || (params.getAll(key).length === 1 && params.get(key) === value);
  return (path.endsWith('/bath-and-body') || params.get('category') === CONDITIONER.category) &&
    exact('category', CONDITIONER.category) && exact('countries', 'canada') &&
    exact('subcategory', 'Conditioner') && exact('subcategories', 'Conditioner') &&
    (params.get('subcategory') === 'Conditioner' || params.get('subcategories') === 'Conditioner');
}
function normalizeParams(input) {
  const p = new URLSearchParams(input);
  for (const [key, value] of [['category', CONDITIONER.category], ['subcategory','Conditioner'], ['subcategories','Conditioner'], ['countries','canada']]) {
    if (p.getAll(key).length === 1 && p.get(key) === value) p.delete(key);
  }
  for (const [alias, key] of [['value','values'],['province','provinces']]) {
    const values = [...p.getAll(key), ...p.getAll(alias)].flatMap(v=>v.split(',')).map(slug).filter(Boolean);
    p.delete(alias);p.delete(key);
    if (values.length) p.set(key,[...new Set(values)].sort().join(','));
  }
  if (p.get('sort') === 'name') p.delete('sort');
  if (p.has('q')) {const q=p.get('q').trim();q?p.set('q',q):p.delete('q');}
  for (const key of filterKeys) if (p.has(key) && !p.get(key).trim()) p.delete(key);
  return p;
}
function normalizeCatalog(products) {
  const eligible = products.filter(p=>String(p.country).toLowerCase()==='canada' && p.category===CONDITIONER.category && (p.subCategory || p.subcategory)===CONDITIONER.subcategory);
  const seen = new Set();
  return eligible.map(p=>{
    const id=productId(p);
    if (!id || seen.has(id) || !p.name) throw new Error('Conditioner catalog has a missing/duplicate identity');
    seen.add(id);
    const rawValues = Array.isArray(p.values) ? p.values : Object.values(p.values || {}).flat();
    const nameSlug=p.name.toLowerCase().replace(/[^a-z0-9\s-]/g,'').replace(/\s+/g,'-');
    return {id,name:p.name,brand:typeof p.brand==='string'?p.brand:(p.brand?.name || p.brand_extracted || ''),
      url:`/country/canada/products/${encodeURIComponent(id)}/name/${nameSlug}`,
      imageUrl:p.imageUrl || '',price:p.price!==null&&p.price!==undefined&&Number.isFinite(Number(p.price))?Number(p.price):null,
      province:p.province || '',values:[...new Set(rawValues.map(slug).filter(Boolean))]};
  });
}
function selectPage(catalog, input) {
  const params=normalizeParams(input);
  const values=(params.get('values')||'').split(',').filter(Boolean);
  const provinces=(params.get('provinces')||'').split(',').filter(Boolean);
  const q=(params.get('q')||'').toLowerCase();
  const cmp=(a,b)=>a<b?-1:a>b?1:0;
  const products=catalog.filter(p=>(!values.length || values.some(v=>p.values.includes(v))) &&
    (!provinces.length || provinces.includes(slug(p.province))) && (!q || `${p.name} ${p.brand}`.toLowerCase().includes(q)) &&
    (!params.get('minPrice') || (p.price!==null && p.price>=Number(params.get('minPrice')))) &&
    (!params.get('maxPrice') || (p.price!==null && p.price<=Number(params.get('maxPrice')))))
    .sort((a,b)=>{
      const sort=params.get('sort');
      if (sort==='price-asc' || sort==='price-desc') {
        if (a.price===null && b.price!==null) return 1;
        if (b.price===null && a.price!==null) return -1;
        const diff=a.price-b.price;
        if (diff) return sort==='price-asc'?diff:-diff;
      }
      return cmp(a.name.toLowerCase(),b.name.toLowerCase()) || cmp(a.id,b.id);
    });
  const pages=Math.max(1,Math.ceil(products.length/CONDITIONER.pageSize));
  const raw=params.getAll('page').length===1?params.get('page'):null;
  const requested=/^[1-9]\d*$/.test(raw||'')?Number(raw):1;
  const page=Math.min(pages,Number.isSafeInteger(requested)?requested:1);
  page===1?params.delete('page'):params.set('page',String(page));
  const filtered=filterKeys.some(k=>params.has(k));
  const canonical=ORIGIN+CONDITIONER.path+(!filtered&&page>1?`?page=${page}`:'');
  return {params,page,pages,total:products.length,products,items:products.slice((page-1)*CONDITIONER.pageSize,page*CONDITIONER.pageSize),
    canonical,title:!filtered&&page>1?CONDITIONER.title.replace(' |',` – Page ${page} |`):CONDITIONER.title};
}
function pageUrl(params,page) {
  const next=new URLSearchParams(params);page===1?next.delete('page'):next.set('page',String(page));
  return CONDITIONER.path+(next.toString()?'?'+next.toString():'');
}
function conditionerSchema(view) {
  return {'@context':'https://schema.org','@type':'CollectionPage',name:'Conditioner',url:view.canonical,mainEntity:{'@type':'ItemList',numberOfItems:view.total,itemListElement:view.items.map((p,i)=>({'@type':'ListItem',position:(view.page-1)*CONDITIONER.pageSize+i+1,url:ORIGIN+p.url,name:p.name}))}};
}
module.exports={CONDITIONER,ORIGIN,filterKeys,slug,escapeHtml,productId,isConditionerLocation,normalizeParams,normalizeCatalog,selectPage,pageUrl,conditionerSchema};
